import * as React from 'react';
import { Sparkles, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PlatformIcon } from '@/components/PlatformIcon';
import { cn } from '@/lib/utils';

/**
 * Lightweight metadata chip — distinct from `Pill` (which is reserved for
 * status). Use Tag for team / channel / niche / type indicators on rows and
 * headers. Carries no semantic meaning beyond "this is a labeled meta value."
 *
 * Channel tones (tiktok / instagram / youtube / twitter / discord) auto-pick
 * a glyph that signals the platform at a glance — pass `icon={false}` to
 * suppress, pass `icon={<Custom />}` to override, or omit `icon` to inherit
 * the default for the tone.
 */

export type TagTone =
  | 'neutral'
  | 'niche'
  | 'creator'
  | 'tiktok'
  | 'instagram'
  | 'youtube'
  | 'twitter'
  | 'discord';

interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: TagTone;
  icon?: React.ReactNode | false;
}

const TONE_CLASSES: Record<TagTone, string> = {
  neutral: 'border-border bg-muted/40 text-foreground/80',
  niche: 'border-brand/30 bg-brand/10 text-brand',
  creator: 'border-info/30 bg-info/10 text-info',
  tiktok: 'border-pink-500/30 bg-pink-500/10 text-pink-700 dark:text-pink-300',
  instagram:
    'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300',
  youtube:
    'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300',
  twitter:
    'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  discord:
    'border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
};

/** Lucide icon defaults for non-channel tones. Channel tones use the
 *  brand-faithful PlatformIcon component instead — handled inline below
 *  because it isn't a LucideIcon. */
const TONE_ICONS: Partial<Record<TagTone, LucideIcon>> = {
  niche: Sparkles,
  creator: Users,
};

const CHANNEL_PLATFORMS: Partial<Record<TagTone, string>> = {
  tiktok: 'TIKTOK',
  instagram: 'INSTAGRAM',
  youtube: 'YOUTUBE',
  twitter: 'TWITTER',
  discord: 'DISCORD',
};

export function Tag({
  tone = 'neutral',
  icon,
  className,
  children,
  ...props
}: TagProps): JSX.Element {
  const platform = CHANNEL_PLATFORMS[tone];
  // Channel tones render icon-only — the platform glyph IS the label. Any
  // children passed in are dropped from the visual chip but rolled into the
  // aria-label / title so screen readers + hover tooltips still announce
  // the platform name.
  const isChannelTone = Boolean(platform);
  const resolvedIcon = (() => {
    if (icon === false) return null;
    if (icon !== undefined) return icon;
    if (platform) return <PlatformIcon platform={platform} size={12} />;
    const DefaultIcon = TONE_ICONS[tone];
    if (DefaultIcon) return <DefaultIcon className="h-3 w-3 shrink-0" strokeWidth={2.25} />;
    return null;
  })();

  const channelAriaLabel =
    isChannelTone && typeof children === 'string'
      ? children
      : isChannelTone && platform
        ? channelLabel(platform)
        : undefined;

  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-md border font-medium leading-none',
        // Icon-only channel chips get tighter square-ish padding so they
        // read as a glyph badge, not a labeled chip with hidden text.
        isChannelTone ? 'gap-0 p-1 text-[0]' : 'gap-1 px-1.5 py-0.5 text-[11px]',
        TONE_CLASSES[tone],
        className,
      )}
      aria-label={channelAriaLabel}
      title={channelAriaLabel ?? props.title}
      {...props}
    >
      {resolvedIcon}
      {!isChannelTone && children}
    </span>
  );
}

/**
 * Map a channel code (TIKTOK / INSTAGRAM / DISCORD) to its themed tone +
 * display label. Falls back to neutral for unknown channels (forward-compat).
 */
export function channelToneFor(channel: string | null | undefined): TagTone {
  switch (channel) {
    case 'TIKTOK':
      return 'tiktok';
    case 'INSTAGRAM':
      return 'instagram';
    case 'YOUTUBE':
      return 'youtube';
    case 'TWITTER':
      return 'twitter';
    case 'DISCORD':
      return 'discord';
    default:
      return 'neutral';
  }
}

export function channelLabel(channel: string | null | undefined): string {
  switch (channel) {
    case 'TIKTOK':
      return 'TikTok';
    case 'INSTAGRAM':
      return 'Instagram';
    case 'YOUTUBE':
      return 'YouTube';
    case 'TWITTER':
      return 'Twitter';
    case 'DISCORD':
      return 'Discord';
    default:
      return channel ?? '—';
  }
}
